import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Loader2, Plus, Printer, Users } from 'lucide-react';
import { CHEMINS, groupesDeLaJournee, listesDeLaJournee, modifierJournee } from '../../../lib/navigation/chemins';
import { euros } from '../../../lib/orders/devis';
import {
  changerLienDesConditions,
  lienDesConditions,
  lireEtatDesRappels,
  lireInscrits,
  lireJournees,
  lireRappels,
  lireReglements,
  type EtatDesRappels,
  type RappelDeFacture,
} from '../../../lib/jpa/gateway.supabase';
import {
  LIBELLE_TARIF_JPA,
  dateCourte,
  dateLongue,
  etatDeLaJournee,
  instantLisible,
  jourABruxelles,
  regrouperParClasse,
  type InscritJpa,
  type JourneeJpa,
  type TypeDeTarifJpa,
} from '../../../lib/jpa/regles';

/*
  Les journées pédagogiques, côté équipe : la liste des journées, puis les
  inscrits de celle qu'on ouvre, par classe, avec leur facture et leur
  règlement. Les listes de présence s'impriment depuis ici.

  Le règlement vient de `reglement_des_inscriptions` — le `settlement` de
  `v_invoice_balances`, celui de l'écran des factures — et non de
  `registrations.payment_status`, que le circuit des paiements ne tient plus.

  Le rappel de paiement part tout seul (`20261019190000`) : l'écran dit où il
  en est — jusqu'où vont les relevés importés, ce qui attend et pourquoi — et,
  pour chaque inscrit, si sa facture a été relancée.
*/

const LIBELLE_ETAT: Record<string, { texte: string; classes: string }> = {
  a_venir: { texte: 'À venir', classes: 'bg-blue-100 text-blue-800' },
  ouverte: { texte: 'Ouverte', classes: 'bg-green-100 text-green-800' },
  fermee: { texte: 'Fermée', classes: 'bg-gray-100 text-gray-700' },
  passee: { texte: 'Passée', classes: 'bg-gray-100 text-gray-700' },
};

/* Les factures échues et non réglées qui attendent encore leur rappel, et pourquoi : [une, plusieurs]. */
const LIBELLE_ATTENTE: Record<string, [string, string]> = {
  a_relancer: ['à relancer dans l’heure', 'à relancer dans l’heure'],
  en_cours: ['en cours d’envoi', 'en cours d’envoi'],
  a_reessayer: ['à réessayer (Gmail a freiné)', 'à réessayer (Gmail a freiné)'],
  attend_le_releve: ['attend un relevé bancaire plus récent', 'attendent un relevé bancaire plus récent'],
  attend_l_annulation: ['attend la décision d’une demande d’annulation', 'attendent la décision d’une demande d’annulation'],
  sans_adresse: ['sans adresse électronique au compte du parent', 'sans adresse électronique au compte du parent'],
};

function texteDuRappel(r: RappelDeFacture): { texte: string; classes: string } {
  if (r.statut === 'envoye' && r.envoyeLe) {
    return { texte: `rappel envoyé ${dateCourte(jourABruxelles(new Date(r.envoyeLe)))}`, classes: 'text-gray-700' };
  }
  if (r.statut === 'echec') return { texte: 'rappel en échec', classes: 'text-red-700 font-semibold' };
  return { texte: 'rappel en cours', classes: 'text-gray-700' };
}

const LIBELLE_REGLEMENT: Record<string, { texte: string; classes: string }> = {
  paid: { texte: 'Payé', classes: 'bg-green-100 text-green-800' },
  partial: { texte: 'Payé en partie', classes: 'bg-amber-100 text-amber-900' },
  unpaid: { texte: 'À payer', classes: 'bg-amber-100 text-amber-900' },
  cancelled: { texte: 'Annulée', classes: 'bg-gray-100 text-gray-700' },
  credited: { texte: 'Créditée', classes: 'bg-gray-100 text-gray-700' },
};

export default function AdminJourneesPage() {
  const [params, setParams] = useSearchParams();
  const [journees, setJournees] = useState<JourneeJpa[]>([]);
  const [inscrits, setInscrits] = useState<InscritJpa[]>([]);
  const [reglements, setReglements] = useState<Map<string, string>>(new Map());
  const [rappels, setRappels] = useState<Map<string, RappelDeFacture>>(new Map());
  /* `undefined` : pas encore lu ; `null` : le rappel n'est pas installé sur cette base ; une chaîne : il est illisible. */
  const [etatRappels, setEtatRappels] = useState<EtatDesRappels | null | string | undefined>(undefined);
  const [lienCG, setLienCG] = useState('');
  const [lienEnregistre, setLienEnregistre] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [voirPassees, setVoirPassees] = useState(false);

  const choisie = params.get('journee');

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const [js, lien] = await Promise.all([lireJournees(), lienDesConditions()]);
      setJournees(js);
      setLienCG(lien ?? '');
      setLienEnregistre(lien);
      const tous = await lireInscrits(js.map((j) => j.sessionId));
      setInscrits(tous);
      setReglements(await lireReglements(tous.filter((i) => i.vivante).map((i) => i.registrationId)));
      /* Le rappel se lit à part : une erreur de sa lecture ne cache pas les inscrits. */
      try {
        const numeros = [...new Set(tous.map((i) => i.invoiceNumber).filter((n): n is string => Boolean(n)))];
        const [lus, etat] = await Promise.all([lireRappels(numeros), lireEtatDesRappels()]);
        setRappels(lus);
        setEtatRappels(etat);
      } catch (e) {
        setEtatRappels(e instanceof Error ? e.message : String(e));
      }
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e));
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { void charger(); }, [charger]);

  const maintenant = new Date();
  const visibles = journees.filter((j) => voirPassees || etatDeLaJournee(j, maintenant) !== 'passee');
  const journee = journees.find((j) => j.sessionId === choisie) ?? visibles[0] ?? null;
  const inscritsDuJour = useMemo(
    () => inscrits.filter((i) => i.sessionId === journee?.sessionId),
    [inscrits, journee],
  );
  const groupes = regrouperParClasse(inscritsDuJour);
  const compte = (s: string) => inscritsDuJour.filter((i) => i.vivante && reglements.get(i.registrationId) === s).length;

  const enregistrerLien = async () => {
    try {
      await changerLienDesConditions(lienCG);
      setLienEnregistre(lienCG.trim() || null);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e));
    }
  };

  if (chargement) {
    return <div className="flex justify-center items-center min-h-[40vh]"><Loader2 className="h-8 w-8 animate-spin text-primary-600" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-end gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Journées pédagogiques</h1>
          <p className="text-gray-600 mt-1">Les journées programmées, leurs inscrits, leurs paiements et leurs listes de présence.</p>
        </div>
        <Link to={CHEMINS.planification.jpaProgrammer} className="btn-primary flex items-center">
          <Plus className="h-5 w-5 mr-2" aria-hidden="true" />
          Programmer une journée
        </Link>
      </div>

      {erreur && (
        <div className="p-4 bg-red-50 text-red-700 rounded-lg flex items-start gap-2" role="alert">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <p>{erreur}</p>
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <label htmlFor="lien-cg" className="text-sm font-semibold text-gray-700">Lien des conditions générales (Google Doc)</label>
          <div className="mt-2 flex gap-2">
            <input
              id="lien-cg"
              type="url"
              value={lienCG}
              onChange={(e) => setLienCG(e.target.value)}
              placeholder="https://docs.google.com/document/d/…"
              className="form-input flex-1"
            />
            <button type="button" onClick={() => void enregistrerLien()} disabled={(lienEnregistre ?? '') === lienCG.trim()} className="btn-outline disabled:opacity-50">
              Enregistrer
            </button>
          </div>
          {!lienEnregistre && <p className="text-sm text-amber-800 mt-2">Aucun lien : les parents voient la case sans pouvoir lire les conditions.</p>}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-sm text-gray-700">
          <p className="font-semibold text-gray-900">Rappel de paiement</p>
          <p className="mt-1">
            Automatique, une seule fois par facture : le lendemain de l’échéance (15 jours après la facture) si le
            paiement n’est pas arrivé, de 9 h à 19 h. Il attend qu’un relevé bancaire importé couvre ce jour-là.
          </p>
          <EtatDesRappelsLu etat={etatRappels} />
        </div>
      </div>

      <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200">
          <h2 className="font-bold text-gray-900">Les journées</h2>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={voirPassees} onChange={(e) => setVoirPassees(e.target.checked)} className="h-4 w-4 rounded border-gray-300" />
            Voir aussi les journées passées
          </label>
        </div>
        {visibles.length === 0 ? (
          <p className="p-4 text-gray-600">Aucune journée programmée.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-700">
                <tr>
                  <th className="px-4 py-2 font-semibold">Journée</th>
                  <th className="px-4 py-2 font-semibold">Lieu</th>
                  <th className="px-4 py-2 font-semibold">Prix normal jusqu’au</th>
                  <th className="px-4 py-2 font-semibold text-right">Inscrits</th>
                  <th className="px-4 py-2 font-semibold">État</th>
                  <th className="px-4 py-2"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((j) => {
                  const etat = LIBELLE_ETAT[etatDeLaJournee(j, maintenant)];
                  const n = inscrits.filter((i) => i.sessionId === j.sessionId && i.vivante).length;
                  const active = j.sessionId === journee?.sessionId;
                  return (
                    <tr key={j.sessionId} className={`border-t border-gray-100 ${active ? 'bg-primary-50' : ''}`}>
                      <td className="px-4 py-3 font-semibold">
                        <button type="button" onClick={() => setParams({ journee: j.sessionId })} className="text-left hover:underline first-letter:uppercase">
                          {dateLongue(j.date)}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        {j.ecole ?? j.centre}
                        {j.avertissement && (
                          <span className="mt-1 flex items-start gap-1 text-xs text-amber-800" title={j.avertissement}>
                            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-px" aria-hidden="true" />
                            <span className="line-clamp-2">{j.avertissement}</span>
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">{instantLisible(j.dateLimite)}</td>
                      <td className="px-4 py-3 text-right font-semibold">{n}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${etat.classes}`}>{etat.texte}</span></td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <Link to={modifierJournee(j.sessionId)} className="text-primary-700 font-semibold hover:underline">Modifier</Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {journee && (
        <section className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
          <div className="flex flex-wrap justify-between items-center gap-3">
            <div>
              <h2 className="text-xl font-bold text-gray-900 first-letter:uppercase">{dateLongue(journee.date)} · {journee.ecole ?? journee.centre}</h2>
              <p className="text-sm text-gray-600">
                {inscritsDuJour.filter((i) => i.vivante).length} inscrits · {compte('paid')} payés · {compte('unpaid') + compte('partial')} à payer
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link to={groupesDeLaJournee(journee.sessionId)} className="btn-primary flex items-center">
                <Users className="h-4 w-4 mr-2" aria-hidden="true" />
                Groupes et listes
              </Link>
              <Link to={listesDeLaJournee(journee.sessionId)} className="btn-outline flex items-center">
                <Printer className="h-4 w-4 mr-2" aria-hidden="true" />
                Listes de présence
              </Link>
              <Link to={modifierJournee(journee.sessionId)} className="btn-outline">Modifier la journée</Link>
              <Link to={CHEMINS.planification.inscriptions} className="btn-outline">Inscrire un enfant</Link>
            </div>
          </div>

          {groupes.length === 0 ? (
            <p className="text-gray-600">Aucun inscrit pour le moment.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-left text-gray-700 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Enfant</th>
                    <th className="px-3 py-2 font-semibold">Parent</th>
                    <th className="px-3 py-2 font-semibold">Tarif</th>
                    <th className="px-3 py-2 font-semibold">Facture</th>
                    <th className="px-3 py-2 font-semibold">Paiement</th>
                  </tr>
                </thead>
                {groupes.map((g) => (
                  <tbody key={g.classe}>
                    <tr className="bg-gray-50">
                      <th colSpan={5} className="px-3 py-2 text-left font-semibold text-gray-800">{g.classe} · {g.inscrits.length}</th>
                    </tr>
                    {g.inscrits.map((i) => {
                      const r = LIBELLE_REGLEMENT[reglements.get(i.registrationId) ?? ''];
                      return (
                        <tr key={i.registrationId} className="border-b border-gray-100">
                          <td className="px-3 py-2 font-semibold">{i.prenom} {i.nom}</td>
                          <td className="px-3 py-2">{i.parent}{i.parentTelephone ? ` · ${i.parentTelephone}` : ''}</td>
                          <td className="px-3 py-2">{LIBELLE_TARIF_JPA[i.priceType as TypeDeTarifJpa] ?? i.priceType} · {euros(i.montantCentimes)}</td>
                          <td className="px-3 py-2">
                            {i.invoiceNumber
                              ? <Link to={`${CHEMINS.comptabilite.factures}?q=${encodeURIComponent(i.invoiceNumber)}`} className="text-primary-700 hover:underline">{i.invoiceNumber}</Link>
                              : '—'}
                          </td>
                          <td className="px-3 py-2">
                            {r ? <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${r.classes}`}>{r.texte}</span> : <span className="text-gray-500">—</span>}
                            {i.invoiceNumber && rappels.get(i.invoiceNumber) && (() => {
                              const t = texteDuRappel(rappels.get(i.invoiceNumber) as RappelDeFacture);
                              return <span className={`block text-xs mt-1 ${t.classes}`}>{t.texte}</span>;
                            })()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                ))}
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

/** Où en est le rappel : relevés importés, envois, attentes. */
function EtatDesRappelsLu({ etat }: { etat: EtatDesRappels | null | string | undefined }) {
  if (etat === undefined) return null;
  if (etat === null) {
    return <p className="mt-2 text-amber-800">Le rappel automatique n’est pas encore installé sur cette base.</p>;
  }
  if (typeof etat === 'string') {
    return <p className="mt-2 text-red-700">État des rappels illisible : {etat}</p>;
  }
  const attentes = Object.entries(LIBELLE_ATTENTE)
    .map(([cle, [une, plusieurs]]) => ({ n: etat.etats[cle] ?? 0, libelle: (etat.etats[cle] ?? 0) > 1 ? plusieurs : une }))
    .filter((a) => a.n > 0);
  return (
    <ul className="mt-2 space-y-0.5">
      <li className={etat.couvertureBancaire ? '' : 'text-amber-800'}>
        {etat.couvertureBancaire
          ? `Relevés bancaires importés jusqu’au ${dateLongue(etat.couvertureBancaire)}.`
          : 'Aucun relevé bancaire importé : aucun rappel ne part.'}
      </li>
      <li>{etat.envoyes} rappel{etat.envoyes > 1 ? 's' : ''} envoyé{etat.envoyes > 1 ? 's' : ''}{etat.dernierEnvoi ? `, le dernier ${instantLisible(etat.dernierEnvoi)}` : ''}.</li>
      {attentes.map((a) => (
        <li key={a.libelle}>{a.n} facture{a.n > 1 ? 's' : ''} {a.libelle}.</li>
      ))}
      {etat.echecs > 0 && (
        <li className="text-red-700 font-semibold">
          {etat.echecs} rappel{etat.echecs > 1 ? 's' : ''} en échec : la raison est dans Communications › Historique.
        </li>
      )}
    </ul>
  );
}
